import { useEffect, useRef } from 'react';
import { useAnchorOptional } from '../context/AnchorContext';
import { SECTION_KEYS, SECTION_TO_ANCHOR_MAP } from '../types';

/**
 * Hook to handle scrolling when entering or exiting edit mode.
 * Automatically derives the anchor href from the current editing section
 * and uses the anchor context for proper scroll coordination.
 *
 * @param isEditing - Whether the section is in edit mode
 * @param sectionKey - The SECTION_KEYS enum value for this section
 *
 * The hook automatically:
 * - Derives the anchor href from the section key using SECTION_TO_ANCHOR_MAP
 * - Uses the AnchorContext to get the scrollToSection function
 * - Handles both entering and exiting edit mode
 * - Falls back to native scrollIntoView if context is not available
 */
export const useScrollToSectionOnEdit = (
  isEditing: boolean,
  sectionKey: SECTION_KEYS
) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const wasEditingRef = useRef(false);
  const anchorContext = useAnchorOptional();

  useEffect(() => {
    // Automatically derive anchor href from section key
    const anchorKey = SECTION_TO_ANCHOR_MAP[sectionKey];
    const anchorHref = anchorKey ? `#${anchorKey}` : null;

    // If anchor context is available, use it for proper anchor coordination
    if (anchorContext?.scrollToSection && anchorHref) {
      // Entering edit mode: scroll to section
      if (isEditing && !wasEditingRef.current) {
        anchorContext.scrollToSection(anchorHref);
      }
      // Exiting edit mode: scroll back to section to keep it in view
      else if (!isEditing && wasEditingRef.current) {
        // Use requestAnimationFrame to wait for DOM update after mode change
        requestAnimationFrame(() => {
          anchorContext.scrollToSection(anchorHref);
        });
      }
    }
    // Fallback to native scrollIntoView (legacy behavior)
    else if (isEditing && !wasEditingRef.current) {
      requestAnimationFrame(() => {
        sectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }

    wasEditingRef.current = isEditing;
  }, [isEditing, sectionKey, anchorContext]);

  return sectionRef;
};
